import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { Heading2, Heading3 } from '../../ui/Typography/Heading';
import { Checkbox } from '../../ui';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { getOrganizations, bulkEditOrganizations } from '../../../redux/organizations/slice';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { selectUpdateConfigLoading } from '../../../redux/config/selectors';
import {
  selectOrganizationsForOnboarding,
  selectOrganizationsLoading,
  selectBulkEditOrganizationsLoading
} from '../../../redux/organizations/selectors';
import { ONBOARDING_STEPS } from '../../../consts/consts';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingStepWrapper,
  OnboardingContentInfo,
  OnboardingContentGroupText,
  OnboardingContentGroupLink,
  OnboardingNotificationRow,
  OnboardingNotificationRowInfo,
  OnboardingNotificationRowActions,
  OnboardingNotificationRowHint,
  OnboardingNotificationEmptyState
} from '../../ui/Layout/OnboardingLayout';

const ONBOARDING_HELP_CENTER_URL =
  'https://help.oqio.de/de/articles/12458332-benachrichtigungen-einstellen?utm_source=onboarding&utm_medium=inproduct';

export const OnboardingStepNotificationsComponent = ({
  updateConfigOnboardingSettings,
  updateConfigLoading,
  organizations,
  organizationsLoading,
  bulkEditOrganizationsLoading,
  getOrganizations,
  bulkEditOrganizations
}) => {
  const [localOrganizations, setLocalOrganizations] = useState([]);
  const [initialOrganizationsState, setInitialOrganizationsState] = useState([]);

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  useEffect(() => {
    if (!organizations) return;
    if (deepEqual(organizations, initialOrganizationsState)) return;
    setLocalOrganizations(organizations);
    setInitialOrganizationsState(organizations);
  }, [organizations, initialOrganizationsState]);

  const handleCheckboxToggle = (orgId, notificationType) => {
    setLocalOrganizations((prev) =>
      prev.map((org) => {
        if (org.id !== orgId) return org;

        if (notificationType === 'portal') {
          return {
            ...org,
            new_messages_worklist_notification: !isPortalNotificationEnabled(org),
            new_states_worklist_notification: !isPortalNotificationEnabled(org)
          };
        }

        if (notificationType === 'email') {
          return {
            ...org,
            new_messages_email_notification: !isEmailNotificationEnabled(org),
            new_states_email_notification: !isEmailNotificationEnabled(org)
          };
        }

        return org;
      })
    );
  };

  const processAndSave = (callback) => {
    const orgsToUpdate = localOrganizations.filter((org) => {
      const initial = initialOrganizationsState.find((initialOrg) => initialOrg.id === org.id);
      return initial && !deepEqual(org, initial);
    });

    if (orgsToUpdate.length === 0) {
      callback?.();
      return;
    }

    bulkEditOrganizations({
      orgsToCreate: [],
      orgsToUpdate,
      callback: () => {
        getOrganizations();
        callback?.();
      }
    });
  };

  const handleBack = () => {
    processAndSave(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.PARTNERS });
    });
  };

  const handleNext = () => {
    processAndSave(() => {
      updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.DATA_SOURCE_SELECT });
    });
  };

  const isPortalNotificationEnabled = (org) => {
    return org.new_messages_worklist_notification || org.new_states_worklist_notification;
  };

  const isEmailNotificationEnabled = (org) => {
    return org.new_messages_email_notification || org.new_states_email_notification;
  };

  const isLoading = updateConfigLoading || organizationsLoading || bulkEditOrganizationsLoading;

  return (
    <>
      <OnboardingNavbar loading={isLoading} />
      <OnboardingProgress />
      <OnboardingContent className="step-notifications">
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Zusammenarbeit auf oqio verwalten</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Wie arbeiten Sie aktuell zusammen? Legen Sie fest, ob die jeweiligen Unternehmen Informationen zu
                Schäden, Risiken und Änderungen ausschließlich im Portal, zusätzlich per E-Mail oder aber über beide
                Kanäle erhalten sollen.
              </OnboardingContentGroupText>
              <OnboardingContentGroupLink href={ONBOARDING_HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
                Zur Erklärung der Benachrichtigungs-Kanäle
              </OnboardingContentGroupLink>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          {localOrganizations && localOrganizations.length > 0 ? (
            localOrganizations.map((org) => (
              <OnboardingContentGroup key={org.id} className="step-notifications">
                <OnboardingNotificationRow>
                  <OnboardingNotificationRowInfo>
                    <Heading3>{org.name}</Heading3>
                    <OnboardingNotificationRowHint>
                      Informationen zu Schäden erhalten über:
                    </OnboardingNotificationRowHint>
                  </OnboardingNotificationRowInfo>
                  <OnboardingNotificationRowActions>
                    <Checkbox
                      checked={isPortalNotificationEnabled(org)}
                      disabled={org.type === 'insurance'}
                      onChange={() => handleCheckboxToggle(org.id, 'portal')}
                    >
                      Ihr Portal
                    </Checkbox>
                    <Checkbox
                      checked={isEmailNotificationEnabled(org)}
                      disabled={org.type === 'insurance'}
                      onChange={() => handleCheckboxToggle(org.id, 'email')}
                    >
                      E-Mail
                    </Checkbox>
                  </OnboardingNotificationRowActions>
                </OnboardingNotificationRow>
              </OnboardingContentGroup>
            ))
          ) : (
            <OnboardingContentGroup className="step-notifications">
              <OnboardingNotificationEmptyState>Keine Unternehmen vorhanden</OnboardingNotificationEmptyState>
            </OnboardingContentGroup>
          )}
        </OnboardingStepWrapper>
        <OnboardingActions onBack={handleBack} onNext={handleNext} loading={isLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  updateConfigLoading: selectUpdateConfigLoading(state),
  organizations: selectOrganizationsForOnboarding(state),
  organizationsLoading: selectOrganizationsLoading(state),
  bulkEditOrganizationsLoading: selectBulkEditOrganizationsLoading(state)
});

const mapDispatchToProps = {
  updateConfigOnboardingSettings,
  getOrganizations,
  bulkEditOrganizations
};

export const OnboardingStepNotifications = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingStepNotificationsComponent);
